import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  GripHorizontal, LayoutGrid, List, Clock, TrendingUp, Briefcase,
  Users, Target, FileCheck, Settings2, ArrowRight, AlertCircle, Inbox, Sparkles,
} from 'lucide-react';
import { logger } from './utils/logger';
import TalentPoolSettings from './TalentPoolSettings';
import { MutualInterestWidget } from './MutualInterest';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Constants — widget catalog (id, label, default visible, default order)
// ---------------------------------------------------------------------------
const WIDGET_CATALOG = [
  { id: 'time_to_hire',      label: 'Time to Hire',       defaultVisible: true,  defaultOrder: 1 },
  { id: 'open_offers',       label: 'Open Offers',        defaultVisible: true,  defaultOrder: 2 },
  { id: 'apps_this_month',   label: 'Applications · MTD', defaultVisible: true,  defaultOrder: 3 },
  { id: 'live_roles_total',  label: 'Live Roles · KPI',   defaultVisible: true,  defaultOrder: 4 },
  { id: 'funnel_snapshot',   label: 'Hiring Funnel',      defaultVisible: true,  defaultOrder: 5 },
  { id: 'pending_scorecards', label: 'Pending Scorecards', defaultVisible: true, defaultOrder: 6 },
  { id: 'live_roles',        label: 'Live Roles',         defaultVisible: true,  defaultOrder: 7 },
  { id: 'new_candidates',    label: 'New Candidates',     defaultVisible: true,  defaultOrder: 8 },
  { id: 'talent_pool',       label: 'Talent Pool Settings', defaultVisible: true, defaultOrder: 9 },
  { id: 'mutual_interest',   label: 'Mutual Interest',    defaultVisible: true,  defaultOrder: 10 },
];

const LAYOUT_STORAGE_KEY = 'vh_recruiter_widget_layout_v1';
const VIEW_TOGGLE_KEY = 'vh_live_roles_view_mode_v1';

function loadLayout() {
  try {
    const raw = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    logger.warn('layout load failed (corrupt or storage unavailable)', e);
  }
  return Object.fromEntries(WIDGET_CATALOG.map((w) => [w.id, w.defaultVisible]));
}

function saveLayout(layout) {
  try {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  } catch (e) {
    logger.warn('layout save failed (quota or storage unavailable)', e);
  }
}

// ---------------------------------------------------------------------------
// Reusable surface
// ---------------------------------------------------------------------------
function WidgetCard({ title, icon: Icon, action, span = 'col-span-12 md:col-span-6 lg:col-span-4', testId, children }) {
  return (
    <section
      data-testid={testId}
      className={`${span} flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden`}
    >
      <header className="flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-slate-400" aria-hidden="true" />}
          <h3 className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 dark:text-slate-400">
            {title}
          </h3>
        </div>
        {action}
      </header>
      <div className="flex-1 p-5">{children}</div>
    </section>
  );
}

function EmptyState({ icon: Icon = Inbox, message }) {
  return (
    <div className="h-full min-h-[120px] flex flex-col items-center justify-center text-slate-400 dark:text-slate-500">
      <Icon className="w-7 h-7 mb-2" aria-hidden="true" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Widgets
// ---------------------------------------------------------------------------
function KPI({ value, sub, accent }) {
  return (
    <div>
      <div className={`text-4xl font-semibold tracking-tighter ${accent || 'text-slate-900 dark:text-slate-50'}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{sub}</div>}
    </div>
  );
}

function TimeToHireWidget({ d }) {
  const tth = d?.time_to_hire || {};
  const target = tth.target_days || 42;
  const value = tth.average_days;
  const onTrack = value != null && value <= target;
  return (
    <WidgetCard title="Time to Hire" icon={Clock} span="col-span-12 md:col-span-6 lg:col-span-3" testId="widget-time-to-hire">
      {value == null ? (
        <KPI value="—" sub={`Target ${target}d · No hires yet`} />
      ) : (
        <>
          <KPI
            value={`${value}d`}
            sub={`${tth.hired_count} hire${tth.hired_count === 1 ? '' : 's'} · target ${target}d`}
            accent={onTrack ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}
          />
          {tth.trend?.length > 1 && <Sparkline values={tth.trend} />}
        </>
      )}
    </WidgetCard>
  );
}

function Sparkline({ values }) {
  // Tiny inline SVG sparkline — no chart lib dependency required.
  if (!values?.length) return null;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * 100;
    const y = 30 - ((v - min) / range) * 30;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox="0 0 100 30" className="mt-3 w-full h-8" preserveAspectRatio="none" aria-hidden="true">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-blue-500" />
    </svg>
  );
}

function OpenOffersWidget({ d }) {
  const o = d?.open_offers || { total: 0, expiring_soon: [] };
  return (
    <WidgetCard title="Open Offers" icon={FileCheck} span="col-span-12 md:col-span-6 lg:col-span-3" testId="widget-open-offers">
      <KPI value={o.total} sub={`${o.expiring_soon.length} expiring within 3 days`} accent={o.expiring_soon.length > 0 ? 'text-amber-600 dark:text-amber-400' : null} />
      {o.expiring_soon.slice(0, 2).map((ex) => (
        <div key={ex.offer_id} className="mt-3 flex items-center justify-between text-xs text-slate-600 dark:text-slate-300">
          <span className="truncate">{ex.candidate_name}</span>
          <span className="text-amber-600 dark:text-amber-400 ml-2 shrink-0">
            {ex.salary_currency} {Number(ex.salary_amount).toLocaleString()}
          </span>
        </div>
      ))}
    </WidgetCard>
  );
}

function AppsMTDWidget({ d }) {
  const t = d?.applications_trend || { this_month: 0, last_month: 0 };
  const delta = t.last_month ? Math.round(100 * (t.this_month - t.last_month) / t.last_month) : null;
  const positive = delta != null && delta >= 0;
  return (
    <WidgetCard title="Applications · MTD" icon={TrendingUp} span="col-span-12 md:col-span-6 lg:col-span-3" testId="widget-apps-mtd">
      <KPI
        value={t.this_month}
        sub={delta == null ? 'No prior month data' : `${positive ? '↑' : '↓'} ${Math.abs(delta)}% vs last month`}
        accent={delta == null ? null : (positive ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')}
      />
    </WidgetCard>
  );
}

function LiveRolesKPIWidget({ d }) {
  return (
    <WidgetCard title="Live Roles" icon={Briefcase} span="col-span-12 md:col-span-6 lg:col-span-3" testId="widget-live-roles-kpi">
      <KPI value={d?.live_roles?.total ?? 0} sub="Actively published" />
    </WidgetCard>
  );
}

function FunnelSnapshotWidget({ d }) {
  const fs = d?.funnel_snapshot;
  if (!fs || fs.total_applications === 0) {
    return (
      <WidgetCard title="Hiring Funnel" icon={Target} span="col-span-12 lg:col-span-6" testId="widget-funnel-snapshot">
        <EmptyState message="No applications yet" />
      </WidgetCard>
    );
  }
  const max = Math.max(...fs.stages.map((s) => s.count), 1);
  return (
    <WidgetCard title="Hiring Funnel" icon={Target} span="col-span-12 lg:col-span-6" testId="widget-funnel-snapshot">
      <div className="space-y-2">
        {fs.stages.map((s) => (
          <div key={s.stage} data-testid={`funnel-bar-${s.stage}`}>
            <div className="flex justify-between text-xs mb-1">
              <span className="capitalize text-slate-600 dark:text-slate-300">{s.stage}</span>
              <span className="font-mono text-slate-500 dark:text-slate-400">{s.count}</span>
            </div>
            <div className="bg-slate-100 dark:bg-slate-800 h-2 rounded">
              <div
                className="bg-blue-600 dark:bg-blue-500 h-full rounded"
                style={{ width: `${(s.count / max) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">{fs.total_applications} total applications</p>
    </WidgetCard>
  );
}

function PendingScorecardsWidget({ d }) {
  const items = d?.pending_scorecards?.items || [];
  return (
    <WidgetCard title="Pending Scorecards" icon={Clock} span="col-span-12 lg:col-span-6" testId="widget-pending-scorecards">
      {items.length === 0 ? (
        <EmptyState message="All scorecards submitted — nice 👌" />
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800 -mx-1">
          {items.slice(0, 5).map((it) => (
            <li key={it.interview_id} className="py-2 px-1 flex items-center justify-between" data-testid={`pending-scorecard-${it.interview_id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate text-slate-900 dark:text-slate-50">{it.candidate_name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {it.role_title || 'Role'} · {it.interview_type || 'Interview'}
                </p>
              </div>
              {it.is_overdue ? (
                <span className="text-xs px-2 py-0.5 rounded bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 shrink-0 ml-2">
                  Overdue
                </span>
              ) : (
                <span className="text-xs text-slate-400 shrink-0 ml-2">{Math.round(it.hours_overdue)}h</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </WidgetCard>
  );
}

function LiveRolesWidget({ d, navigate }) {
  const [view, setView] = useState(() => {
    try { return window.localStorage.getItem(VIEW_TOGGLE_KEY) || 'tile'; }
    catch (e) { logger.warn('view toggle load failed', e); return 'tile'; }
  });
  const setViewPersist = (v) => {
    setView(v);
    try { window.localStorage.setItem(VIEW_TOGGLE_KEY, v); }
    catch (e) { logger.warn('view toggle save failed', e); }
  };
  const items = d?.live_roles?.items || [];

  const toggle = (
    <div role="tablist" aria-label="Switch live roles view" className="flex bg-slate-100 dark:bg-slate-800 rounded-md p-0.5" data-testid="live-roles-view-toggle">
      <button
        role="tab"
        aria-selected={view === 'tile'}
        onClick={() => setViewPersist('tile')}
        data-testid="live-roles-tile-btn"
        className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition ${view === 'tile' ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}
      >
        <LayoutGrid className="w-3.5 h-3.5" aria-hidden="true" /> Tile
      </button>
      <button
        role="tab"
        aria-selected={view === 'list'}
        onClick={() => setViewPersist('list')}
        data-testid="live-roles-list-btn"
        className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition ${view === 'list' ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}
      >
        <List className="w-3.5 h-3.5" aria-hidden="true" /> List
      </button>
    </div>
  );

  return (
    <WidgetCard title={`Live Roles · ${items.length}`} icon={Briefcase} action={toggle} span="col-span-12 lg:col-span-8" testId="widget-live-roles">
      {items.length === 0 ? (
        <EmptyState message="No live roles. Publish a requisition to see it here." />
      ) : view === 'tile' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="live-roles-tile-view">
          {items.slice(0, 6).map((r) => (
            <button
              key={r.id}
              data-testid={`live-role-tile-${r.id}`}
              onClick={() => navigate('/cockpit')}
              className="text-left p-3 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <p className="text-sm font-medium text-slate-900 dark:text-slate-50 truncate">{r.title}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {r.business_line || '—'} · {r.country || '—'} · {r.seniority || '—'}
              </p>
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-300">
                  <Users className="w-3 h-3" aria-hidden="true" /> {r.applicant_count} applicant{r.applicant_count === 1 ? '' : 's'}
                </span>
                <span className="text-slate-400">{r.days_active}d active</span>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto -mx-1" data-testid="live-roles-list-view">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800">
                <th className="py-2 pr-3 font-medium">Role</th>
                <th className="py-2 pr-3 font-medium">Business line</th>
                <th className="py-2 pr-3 font-medium text-right">Applicants</th>
                <th className="py-2 pr-3 font-medium text-right">Days</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.slice(0, 10).map((r) => (
                <tr key={r.id} data-testid={`live-role-row-${r.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer" onClick={() => navigate('/cockpit')}>
                  <td className="py-2 pr-3 text-slate-900 dark:text-slate-50">{r.title}</td>
                  <td className="py-2 pr-3 text-slate-500 dark:text-slate-400">{r.business_line || '—'}</td>
                  <td className="py-2 pr-3 text-right text-slate-700 dark:text-slate-200">{r.applicant_count}</td>
                  <td className="py-2 pr-3 text-right text-slate-500 dark:text-slate-400">{r.days_active}d</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </WidgetCard>
  );
}

function NewCandidatesWidget({ d, navigate }) {
  const items = d?.new_candidates?.items || [];
  return (
    <WidgetCard
      title="New This Week"
      icon={Users}
      span="col-span-12 lg:col-span-4"
      testId="widget-new-candidates"
      action={items.length > 0 ? (
        <button onClick={() => navigate('/cockpit')} className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1" data-testid="new-candidates-view-all">
          View all <ArrowRight className="w-3 h-3" aria-hidden="true" />
        </button>
      ) : null}
    >
      {items.length === 0 ? (
        <EmptyState message="No new candidates this week" />
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800 -mx-1">
          {items.slice(0, 6).map((c) => (
            <li key={c.application_id} className="py-2 px-1" data-testid={`new-candidate-${c.application_id}`}>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-50 truncate">{c.candidate_name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{c.role_title || 'Role'} · {c.stage}</p>
            </li>
          ))}
        </ul>
      )}
    </WidgetCard>
  );
}

// ---------------------------------------------------------------------------
// Configure-layout drawer
// ---------------------------------------------------------------------------
function ConfigureDrawer({ open, onClose, layout, setLayout }) {
  if (!open) return null;
  const toggle = (id) => {
    const next = { ...layout, [id]: !layout[id] };
    setLayout(next);
    saveLayout(next);
  };
  const reset = () => {
    const fresh = Object.fromEntries(WIDGET_CATALOG.map((w) => [w.id, w.defaultVisible]));
    setLayout(fresh);
    saveLayout(fresh);
  };
  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900/30 z-40"
        onClick={onClose}
        role="presentation"
      />
      <aside
        role="dialog"
        aria-label="Configure dashboard layout"
        data-testid="configure-layout-drawer"
        className="fixed inset-y-0 right-0 w-full max-w-sm bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 z-50 shadow-xl flex flex-col"
      >
        <header className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">Configure dashboard</h2>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">✕</button>
        </header>
        <div className="flex-1 overflow-y-auto p-5">
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            Toggle widgets on or off. Your layout is saved automatically on this device.
          </p>
          <ul className="space-y-2">
            {WIDGET_CATALOG.map((w) => (
              <li key={w.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-800">
                <span className="text-sm text-slate-900 dark:text-slate-50">{w.label}</span>
                <button
                  onClick={() => toggle(w.id)}
                  role="switch"
                  aria-checked={!!layout[w.id]}
                  data-testid={`widget-toggle-${w.id}`}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${layout[w.id] ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${layout[w.id] ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </li>
            ))}
          </ul>
        </div>
        <footer className="p-5 border-t border-slate-200 dark:border-slate-800 flex justify-between">
          <button onClick={reset} data-testid="reset-layout-btn" className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-200">Reset to default</button>
          <button onClick={onClose} className="text-sm px-4 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700">Done</button>
        </footer>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main exported dashboard
// ---------------------------------------------------------------------------
export default function RecruiterWidgetDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [layout, setLayout] = useState(loadLayout);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/recruiter/widgets/summary`);
      setData(data); setError('');
    } catch (err) {
      logger.error('widget summary', err);
      setError(err.response?.data?.detail || 'Unable to load dashboard.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    return WIDGET_CATALOG.filter((w) => layout[w.id] !== false);
  }, [layout]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50" data-testid="recruiter-widget-dashboard">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="dashboard-title">Recruiter Dashboard</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {data?.generated_at ? `Updated ${new Date(data.generated_at).toLocaleTimeString()}` : 'Loading…'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/cockpit')}
              data-testid="open-cockpit-btn"
              className="px-3 py-1.5 text-sm rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition flex items-center gap-2"
            >
              <Briefcase className="w-4 h-4" aria-hidden="true" /> Vault Hub
            </button>
            <button
              onClick={() => navigate('/pm')}
              data-testid="employer-pm-link"
              className="px-3 py-1.5 text-sm rounded-md border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition flex items-center gap-2"
            >
              <Briefcase className="w-4 h-4" aria-hidden="true" /> My Implementation
            </button>
            <button
              onClick={() => setDrawerOpen(true)}
              data-testid="configure-layout-btn"
              className="px-3 py-1.5 text-sm rounded-md bg-slate-900 dark:bg-slate-50 text-white dark:text-slate-900 hover:opacity-90 transition flex items-center gap-2"
            >
              <Settings2 className="w-4 h-4" aria-hidden="true" /> Configure
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 text-sm flex items-center gap-2" data-testid="dashboard-error">
            <AlertCircle className="w-4 h-4" aria-hidden="true" /> {error}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-4 md:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="col-span-12 md:col-span-6 lg:col-span-4 h-32 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-4 md:gap-6 auto-rows-min"
            data-testid="widget-grid"
          >
            {visible.map((w) => {
              const props = { d: data, navigate };
              switch (w.id) {
                case 'time_to_hire':      return <TimeToHireWidget key={w.id} {...props} />;
                case 'open_offers':       return <OpenOffersWidget key={w.id} {...props} />;
                case 'apps_this_month':   return <AppsMTDWidget key={w.id} {...props} />;
                case 'live_roles_total':  return <LiveRolesKPIWidget key={w.id} {...props} />;
                case 'funnel_snapshot':   return <FunnelSnapshotWidget key={w.id} {...props} />;
                case 'pending_scorecards': return <PendingScorecardsWidget key={w.id} {...props} />;
                case 'live_roles':        return <LiveRolesWidget key={w.id} {...props} />;
                case 'new_candidates':    return <NewCandidatesWidget key={w.id} {...props} />;
                case 'talent_pool':       return <TalentPoolSettings key={w.id} />;
                case 'mutual_interest':   return <MutualInterestWidget key={w.id} />;
                default: return null;
              }
            })}
            {visible.length === 0 && (
              <div className="col-span-12 py-16 text-center text-slate-500" data-testid="no-widgets-empty-state">
                <Sparkles className="w-7 h-7 mx-auto mb-2" aria-hidden="true" />
                <p className="text-sm">All widgets hidden. Click <strong>Configure</strong> to enable some.</p>
              </div>
            )}
          </div>
        )}
      </main>

      <ConfigureDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} layout={layout} setLayout={setLayout} />
    </div>
  );
}
